import { useState, useMemo } from 'react'
import { useUrlFilters } from '../../hooks/useUrlFilters'
import { useQuery } from '@tanstack/react-query'
import {
  TrendingUp,
  DollarSign,
  Users,
  BarChart3,
  Target,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend,
} from 'recharts'
import { metaApi, metaAnalyticsApi } from '../../api/endpoints'
import Badge from '../../components/ui/Badge'
import LoadingState from '../../components/ui/LoadingState'
import { useScoringSegments } from '../../hooks/usePipelineStages'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtINR(n: unknown): string {
  if (n == null) return '--'
  return `\u20B9${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function fmtNum(n: unknown): string {
  if (n == null) return '0'
  return Number(n).toLocaleString('en-IN')
}

function fmtPct(n: unknown): string {
  if (n == null) return '--'
  return `${Number(n).toFixed(2)}%`
}

function statusVariant(s: string): 'success' | 'warning' | 'default' | 'danger' {
  const v = (s || '').toUpperCase()
  if (v === 'ACTIVE') return 'success'
  if (v === 'PAUSED') return 'warning'
  return 'default'
}

// Default date range: last 90 days
function defaultDates(): { from: string; to: string } {
  const now = new Date()
  const from = new Date(now)
  from.setDate(from.getDate() - 90)
  return {
    from: from.toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
  }
}

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------

function KpiCard({
  label, value, subtext, icon: Icon, color = 'text-indigo-600',
}: {
  label: string
  value: string
  subtext?: string
  icon: typeof DollarSign
  color?: string
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</span>
        <Icon size={16} className={color} />
      </div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Campaign Table
// ---------------------------------------------------------------------------

type SegmentDisplay = { key: string; label: string; color: string; bgLight: string; leadField: string }

type SortKey = 'spend' | 'total_leads' | 'cpl' | 'cpql' | 'cps' | 'roas' | 'qualified' | 'won'

function CampaignTable({ campaigns, segmentDisplay }: { campaigns: any[]; segmentDisplay: SegmentDisplay[] }) {
  const [sortKey, setSortKey] = useState<SortKey>('spend')
  const [sortAsc, setSortAsc] = useState(false)

  const sorted = [...campaigns].sort((a, b) => {
    const va = a[sortKey] ?? (sortAsc ? Infinity : -Infinity)
    const vb = b[sortKey] ?? (sortAsc ? Infinity : -Infinity)
    return sortAsc ? va - vb : vb - va
  })

  function SortHeader({ label, field }: { label: string; field: SortKey }) {
    const active = sortKey === field
    return (
      <th
        className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 whitespace-nowrap"
        onClick={() => {
          if (active) setSortAsc(!sortAsc)
          else { setSortKey(field); setSortAsc(false) }
        }}
      >
        <span className="flex items-center gap-1">
          {label}
          {active && (sortAsc ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
        </span>
      </th>
    )
  }

  return (
    <>
      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-gray-100">
        {sorted.map((c: any) => (
          <div key={c.campaign_id} className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900 truncate mr-2">{c.campaign_name}</h3>
              <Badge variant={statusVariant(c.status)}>{c.status}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <p className="text-xs text-gray-500">Spend</p>
                <p className="text-sm font-semibold text-gray-900">{fmtINR(c.spend)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Leads</p>
                <p className="text-sm font-medium text-gray-700">{fmtNum(c.total_leads)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">CPL</p>
                <p className="text-sm font-medium text-gray-700">{fmtINR(c.cpl)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">ROAS</p>
                <p className="text-sm font-medium">
                  {c.roas != null ? (
                    <span className={c.roas >= 1 ? 'text-green-600' : 'text-red-600'}>{c.roas}x</span>
                  ) : '--'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Qualified</p>
                <p className="text-sm font-medium text-gray-700">{fmtNum(c.qualified)} ({fmtINR(c.cpql)} CPQL)</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Won</p>
                <p className="text-sm font-medium text-gray-700">{fmtNum(c.won)} ({fmtINR(c.cps)} CPS)</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {segmentDisplay.map((seg) => {
                const count = c[seg.leadField]
                if (!count || count <= 0) return null
                return (
                  <span key={seg.key} className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ color: seg.color, backgroundColor: seg.bgLight }}>
                    {count} {seg.label.toUpperCase()}
                  </span>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <SortHeader label="Spend" field="spend" />
              <SortHeader label="Leads" field="total_leads" />
              <SortHeader label="CPL" field="cpl" />
              <SortHeader label="Qualified" field="qualified" />
              <SortHeader label="CPQL" field="cpql" />
              <SortHeader label="Won" field="won" />
              <SortHeader label="CPS" field="cps" />
              <SortHeader label="ROAS" field="roas" />
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quality</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.map((c: any) => (
              <tr key={c.campaign_id} className="hover:bg-gray-50">
                <td className="px-3 py-2.5 text-sm font-medium text-gray-900 max-w-[200px] truncate" title={c.campaign_name}>
                  {c.campaign_name}
                </td>
                <td className="px-3 py-2.5">
                  <Badge variant={statusVariant(c.status)}>{c.status}</Badge>
                </td>
                <td className="px-3 py-2.5 text-sm text-gray-700 font-medium">{fmtINR(c.spend)}</td>
                <td className="px-3 py-2.5 text-sm text-gray-700">{fmtNum(c.total_leads)}</td>
                <td className="px-3 py-2.5 text-sm text-gray-700">{fmtINR(c.cpl)}</td>
                <td className="px-3 py-2.5 text-sm text-gray-700">{fmtNum(c.qualified)}</td>
                <td className="px-3 py-2.5 text-sm text-gray-700">{fmtINR(c.cpql)}</td>
                <td className="px-3 py-2.5 text-sm text-gray-700">{fmtNum(c.won)}</td>
                <td className="px-3 py-2.5 text-sm text-gray-700">{fmtINR(c.cps)}</td>
                <td className="px-3 py-2.5 text-sm font-medium">
                  {c.roas != null ? (
                    <span className={c.roas >= 1 ? 'text-green-600' : 'text-red-600'}>{c.roas}x</span>
                  ) : '--'}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1">
                    {segmentDisplay.map((seg) => {
                      const count = c[seg.leadField]
                      if (!count || count <= 0) return null
                      return (
                        <span key={seg.key} className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ color: seg.color, backgroundColor: seg.bgLight }}>
                          {count} {seg.label.toUpperCase()}
                        </span>
                      )
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Lead Quality Table (grouped by campaign / ad set / ad / form / city)
// ---------------------------------------------------------------------------

const GROUP_OPTIONS = [
  { value: 'campaign', label: 'Campaign' },
  { value: 'adset', label: 'Ad Set' },
  { value: 'ad', label: 'Ad' },
  { value: 'form', label: 'Form' },
  { value: 'city', label: 'City' },
] as const

type GroupBy = (typeof GROUP_OPTIONS)[number]['value']

type LQSortKey =
  | 'total_leads' | 'qualified' | 'qualification_rate' | 'avg_lead_score'
  | 'won' | 'revenue' | 'spend' | 'cpql' | 'roas'

function LeadQualityTable({ rows, segmentDisplay }: { rows: any[]; segmentDisplay: SegmentDisplay[] }) {
  const [sortKey, setSortKey] = useState<LQSortKey>('total_leads')
  const [sortAsc, setSortAsc] = useState(false)

  const sorted = [...rows].sort((a, b) => {
    const va = a[sortKey] ?? (sortAsc ? Infinity : -Infinity)
    const vb = b[sortKey] ?? (sortAsc ? Infinity : -Infinity)
    return sortAsc ? va - vb : vb - va
  })

  function SortHeader({ label, field }: { label: string; field: LQSortKey }) {
    const active = sortKey === field
    return (
      <th
        className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 whitespace-nowrap"
        onClick={() => {
          if (active) setSortAsc(!sortAsc)
          else { setSortKey(field); setSortAsc(false) }
        }}
      >
        <span className="flex items-center gap-1">
          {label}
          {active && (sortAsc ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
        </span>
      </th>
    )
  }

  function QualityBadges({ r }: { r: any }) {
    return (
      <div className="flex items-center gap-1">
        {segmentDisplay.map((seg) => {
          const count = r[seg.leadField]
          if (!count || count <= 0) return null
          return (
            <span key={seg.key} className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ color: seg.color, backgroundColor: seg.bgLight }}>
              {count} {seg.label.toUpperCase()}
            </span>
          )
        })}
      </div>
    )
  }

  return (
    <>
      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-gray-100">
        {sorted.map((r: any) => (
          <div key={r.group_key} className="p-4">
            <h3 className="text-sm font-semibold text-gray-900 truncate mb-3" title={r.group_name}>{r.group_name}</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><p className="text-xs text-gray-500">Leads</p><p className="text-sm font-medium text-gray-700">{fmtNum(r.total_leads)}</p></div>
              <div><p className="text-xs text-gray-500">Qualified</p><p className="text-sm font-medium text-gray-700">{fmtNum(r.qualified)} ({fmtPct(r.qualification_rate)})</p></div>
              <div><p className="text-xs text-gray-500">Avg Score</p><p className="text-sm font-medium text-gray-700">{r.avg_lead_score != null ? r.avg_lead_score : '--'}</p></div>
              <div><p className="text-xs text-gray-500">Won</p><p className="text-sm font-medium text-gray-700">{fmtNum(r.won)}</p></div>
              <div><p className="text-xs text-gray-500">Revenue</p><p className="text-sm font-medium text-gray-700">{fmtINR(r.revenue)}</p></div>
              <div><p className="text-xs text-gray-500">CPQL</p><p className="text-sm font-medium text-gray-700">{fmtINR(r.cpql)}</p></div>
              <div><p className="text-xs text-gray-500">Spend</p><p className="text-sm font-medium text-gray-700">{fmtINR(r.spend)}</p></div>
              <div>
                <p className="text-xs text-gray-500">ROAS</p>
                <p className="text-sm font-medium">{r.roas != null ? <span className={r.roas >= 1 ? 'text-green-600' : 'text-red-600'}>{r.roas}x</span> : '--'}</p>
              </div>
            </div>
            <QualityBadges r={r} />
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <SortHeader label="Leads" field="total_leads" />
              <SortHeader label="Qualified" field="qualified" />
              <SortHeader label="Qual %" field="qualification_rate" />
              <SortHeader label="Avg Score" field="avg_lead_score" />
              <SortHeader label="Won" field="won" />
              <SortHeader label="Revenue" field="revenue" />
              <SortHeader label="Spend" field="spend" />
              <SortHeader label="CPQL" field="cpql" />
              <SortHeader label="ROAS" field="roas" />
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quality</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.map((r: any) => (
              <tr key={r.group_key} className="hover:bg-gray-50">
                <td className="px-3 py-2.5 text-sm font-medium text-gray-900 max-w-[220px] truncate" title={r.group_name}>{r.group_name}</td>
                <td className="px-3 py-2.5 text-sm text-gray-700">{fmtNum(r.total_leads)}</td>
                <td className="px-3 py-2.5 text-sm text-gray-700">{fmtNum(r.qualified)}</td>
                <td className="px-3 py-2.5 text-sm text-gray-700">{fmtPct(r.qualification_rate)}</td>
                <td className="px-3 py-2.5 text-sm text-gray-700">{r.avg_lead_score != null ? r.avg_lead_score : '--'}</td>
                <td className="px-3 py-2.5 text-sm text-gray-700">{fmtNum(r.won)}</td>
                <td className="px-3 py-2.5 text-sm text-gray-700">{fmtINR(r.revenue)}</td>
                <td className="px-3 py-2.5 text-sm text-gray-700">{fmtINR(r.spend)}</td>
                <td className="px-3 py-2.5 text-sm text-gray-700">{fmtINR(r.cpql)}</td>
                <td className="px-3 py-2.5 text-sm font-medium">
                  {r.roas != null ? <span className={r.roas >= 1 ? 'text-green-600' : 'text-red-600'}>{r.roas}x</span> : '--'}
                </td>
                <td className="px-3 py-2.5"><QualityBadges r={r} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Recharts custom tooltip
// ---------------------------------------------------------------------------

function TrendTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-900 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-gray-500">{p.name}:</span>
          <span className="font-medium text-gray-900">
            {p.dataKey === 'spend' || p.dataKey === 'cpl' ? fmtINR(p.value) : fmtNum(p.value)}
          </span>
        </p>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

// Map segment key to the API response field name for lead counts
function segmentLeadField(key: string): string {
  return `${key.toLowerCase()}_leads`
}

// Lighten a hex color for background usage
function hexToLightBg(hex: string): string {
  // Append low-opacity suffix for inline use
  return `${hex}15`
}

export default function MetaAnalyticsPage() {
  const scoringSegments = useScoringSegments()
  const segmentDisplay: SegmentDisplay[] = useMemo(() => {
    if (scoringSegments.length > 0) {
      return scoringSegments.map((s) => ({
        key: s.segment,
        label: s.label,
        color: s.color || '#6b7280',
        bgLight: hexToLightBg(s.color || '#6b7280'),
        leadField: segmentLeadField(s.segment),
      }))
    }
    return [
      { key: 'HOT', label: 'Hot', color: '#dc2626', bgLight: '#fef2f2', leadField: 'hot_leads' },
      { key: 'WARM', label: 'Warm', color: '#d97706', bgLight: '#fffbeb', leadField: 'warm_leads' },
      { key: 'COLD', label: 'Cold', color: '#9ca3af', bgLight: '#f9fafb', leadField: 'cold_leads' },
    ]
  }, [scoringSegments])

  const defaults = defaultDates()
  const [filters, setFilter] = useUrlFilters({ date_from: defaults.from, date_to: defaults.to, connection_id: '' })
  const dateFrom = filters.date_from
  const dateTo = filters.date_to
  const setDateFrom = (v: string) => setFilter('date_from', v)
  const setDateTo = (v: string) => setFilter('date_to', v)
  const connectionId = filters.connection_id ? Number(filters.connection_id) : undefined
  const setConnectionId = (v: string) => setFilter('connection_id', v)

  // Fetch Meta connections for the account selector
  const { data: connectionsData } = useQuery({
    queryKey: ['meta-connections'],
    queryFn: () => metaApi.listConnections(),
    staleTime: 30 * 60 * 1000, // 30 min — connections rarely change
  })
  const connections: Array<{ id: number; label: string | null; brand: string | null; page_name: string | null; status: string }> =
    (connectionsData?.data as any) || []

  const { data: overviewData, isLoading: overviewLoading } = useQuery({
    queryKey: ['meta-analytics-overview', dateFrom, dateTo, connectionId],
    queryFn: () => metaAnalyticsApi.getOverview(dateFrom, dateTo, connectionId),
    staleTime: 5 * 60 * 1000,
  })

  const { data: campaignsData, isLoading: campaignsLoading } = useQuery({
    queryKey: ['meta-analytics-campaigns', dateFrom, dateTo, connectionId],
    queryFn: () => metaAnalyticsApi.getCampaigns(dateFrom, dateTo, connectionId),
    staleTime: 5 * 60 * 1000,
  })

  const { data: trendsData, isLoading: trendsLoading } = useQuery({
    queryKey: ['meta-analytics-trends', dateFrom, dateTo, connectionId],
    queryFn: () => metaAnalyticsApi.getTrends(dateFrom, dateTo, undefined, connectionId),
    staleTime: 5 * 60 * 1000,
  })

  const [groupBy, setGroupBy] = useState<GroupBy>('ad')
  const { data: leadQualityData, isLoading: lqLoading } = useQuery({
    queryKey: ['meta-lead-quality', dateFrom, dateTo, groupBy, connectionId],
    queryFn: () => metaAnalyticsApi.getLeadQuality(dateFrom, dateTo, groupBy, connectionId),
    staleTime: 5 * 60 * 1000,
  })

  const overview: any = overviewData?.data || {}
  const campaigns: any[] = (campaignsData?.data as any[]) || []
  const trends: any[] = (trendsData?.data as any[]) || []
  const leadQuality: any[] = (leadQualityData?.data as any[]) || []
  const groupLabel = GROUP_OPTIONS.find((o) => o.value === groupBy)?.label ?? 'Ad'

  return (
    <div className="space-y-6">
      {/* Header + Date Range */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ad Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">Campaign performance, funnel metrics, and spend analysis</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Account selector */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <select
                value={filters.connection_id}
                onChange={(e) => setConnectionId(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 pr-8 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white appearance-none min-w-[180px]"
              >
                <option value="">All Accounts</option>
                {connections.map((conn) => (
                  <option key={conn.id} value={String(conn.id)}>
                    {conn.page_name || conn.label || conn.brand || `Account #${conn.id}`}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
            {connectionId != null && (() => {
              const sel = connections.find((c) => c.id === connectionId)
              return sel ? <Badge variant={statusVariant(sel.status)}>{sel.status}</Badge> : null
            })()}
          </div>

          {/* Date range */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-full sm:w-auto"
            />
            <span className="text-gray-400 hidden sm:inline">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-full sm:w-auto"
            />
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {overviewLoading ? (
        <LoadingState message="Loading overview..." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <KpiCard icon={DollarSign} label="Total Spend" value={fmtINR(overview.spend)} subtext={`${fmtNum(overview.impressions)} impressions`} color="text-indigo-600" />
          <KpiCard icon={Users} label="Leads" value={fmtNum(overview.total_leads)} subtext={`CPL: ${fmtINR(overview.cpl)}`} color="text-blue-600" />
          <KpiCard icon={Target} label="Qualified" value={fmtNum(overview.qualified_leads)} subtext={`CPQL: ${fmtINR(overview.cpql)}`} color="text-violet-600" />
          <KpiCard icon={BarChart3} label="Deals Won" value={fmtNum(overview.deals_won)} subtext={`CPS: ${fmtINR(overview.cps)}`} color="text-green-600" />
          <KpiCard icon={TrendingUp} label="ROAS" value={overview.roas != null ? `${overview.roas}x` : '--'} subtext={`Revenue: ${fmtINR(overview.revenue)}`} color="text-emerald-600" />
        </div>
      )}

      {/* Funnel rates row */}
      {!overviewLoading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          <MiniMetric label="CTR" value={fmtPct(overview.ctr)} />
          <MiniMetric label="CPC" value={fmtINR(overview.cpc)} />
          <MiniMetric label="Meta Conversions" value={fmtNum(overview.meta_conversions)} />
          <MiniMetric label="CRM Leads" value={fmtNum(overview.total_leads)} />
          <MiniMetric label="Lead > Qualified" value={fmtPct(overview.lead_to_qualified_rate)} />
          <MiniMetric label="Qualified > Won" value={fmtPct(overview.qualified_to_won_rate)} />
          <MiniMetric label="Avg Score" value={overview.avg_lead_score != null ? String(overview.avg_lead_score) : '--'} />
          <MiniMetric label="Quality" value={segmentDisplay.map((s) => `${overview[s.leadField] || 0}${s.label.charAt(0)}`).join(' / ')} />
        </div>
      )}

      {/* Spend Trend Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Daily Spend & CPL Trend</h2>
        {trendsLoading ? (
          <LoadingState message="Loading trends..." />
        ) : trends.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-400">No trend data for this period</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trends} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} interval="preserveStartEnd" />
              <YAxis yAxisId="spend" tick={{ fontSize: 10 }} width={48} tickFormatter={(v: number) => v >= 100000 ? `₹${(v/100000).toFixed(1)}L` : v >= 1000 ? `₹${(v/1000).toFixed(0)}K` : `₹${v}`} />
              <YAxis yAxisId="cpl" orientation="right" tick={{ fontSize: 10 }} width={48} tickFormatter={(v: number) => v >= 100000 ? `₹${(v/100000).toFixed(1)}L` : v >= 1000 ? `₹${(v/1000).toFixed(0)}K` : `₹${v}`} />
              <Tooltip content={<TrendTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area yAxisId="spend" type="monotone" dataKey="spend" name="Spend" stroke="#6366f1" fill="url(#spendGrad)" strokeWidth={2} />
              <Area yAxisId="cpl" type="monotone" dataKey="cpl" name="CPL" stroke="#f59e0b" fill="none" strokeWidth={2} strokeDasharray="4 4" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Impressions & Clicks Chart */}
      {!trendsLoading && trends.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-4">Impressions & Clicks</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trends} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} width={40} tickFormatter={(v: number) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
              <Tooltip content={<TrendTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="impressions" name="Impressions" fill="#c7d2fe" radius={[3, 3, 0, 0]} />
              <Bar dataKey="clicks" name="Clicks" fill="#6366f1" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Campaign Comparison Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">Campaign Comparison</h2>
          <p className="text-xs text-gray-400 mt-0.5">{campaigns.length} campaigns — sorted by spend. Click column headers to re-sort.</p>
        </div>
        {campaignsLoading ? (
          <LoadingState message="Loading campaigns..." />
        ) : campaigns.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-400">No campaign data</div>
        ) : (
          <CampaignTable campaigns={campaigns} segmentDisplay={segmentDisplay} />
        )}
      </div>

      {/* Lead Quality by dimension */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Lead Quality by {groupLabel}</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              How leads from each {groupLabel.toLowerCase()} performed in the CRM — qualification, revenue, and cost per qualified lead.
            </p>
          </div>
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 self-start">
            {GROUP_OPTIONS.map((o) => (
              <button
                key={o.value}
                onClick={() => setGroupBy(o.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  groupBy === o.value ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        {lqLoading ? (
          <LoadingState message="Loading lead quality..." />
        ) : leadQuality.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-400">No lead data for this period</div>
        ) : (
          <LeadQualityTable rows={leadQuality} segmentDisplay={segmentDisplay} />
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mini metric badge
// ---------------------------------------------------------------------------

function MiniMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-3 py-2 text-center">
      <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-semibold text-gray-900 mt-0.5">{value}</p>
    </div>
  )
}
